import React, { useEffect, useState, useMemo } from "react";
import { createStructuredSelector } from "reselect";
import { connect } from "react-redux";
import moment from "moment";

import Drawer from "components/Drawer";
import Button from "components/Button";
import ZoneForm from "../ZoneForm";

import { useStateSelector } from "utils/field";
import { initForm, resetForm } from "store/form/actions";
import { checkRequirements } from "utils/singleZoneCooling";

import classes from "./styles.module.scss";
import globalStyles from "components/globalStyles.module.scss";

const CreateZoneDrawer = ({
    isOpen,
    onClose,
    change,
    zoneIdToEdit,
    displayUnits,
    resetForm,
    initForm,
}) => {
    const { coolingZones = {} } = useStateSelector(`model.modelData`) || {};
    
    const {
        label: zoneLabel = "",
        autoGenerateLabel = false,
        selectedStorey = null,
        selectedRooms = [],
        selectedWalls = [],
        isFullyEnclosed = false,
        measurements = {
            totalFloorArea: 0,
            totalWallPerimeter: 0,
        },
        requirements = {
            hasMinArea: false,
            hasWallOutlet: false,
            hasOtherElectrical: false,
            hasVentilation: false,
            ventilationType: null,
        },
        warnings = {
            missingInteriorDoor: false,
            notFullyEnclosed: false,
            roomsWithIntWalls: [],
        },
        systemServesZone = false,
        upgradePackageId = "",
    } = useStateSelector(`newForm.coolingZoneDrawer`) || {};

    // Check if all requirements are met
    const hasAllRequirements = useMemo(() => checkRequirements(requirements), [requirements]);

    const [saving, toggleSaving] = useState(false);

    useEffect(() => {
        // Reset the form when the drawer is closed
        if (!isOpen) {
            resetForm("coolingZoneDrawer");
        }

        // Initialize the form when the drawer is opened
        if (isOpen) {
            if (zoneIdToEdit) {
                initForm("coolingZoneDrawer", coolingZones[zoneIdToEdit]);
            }
            else {
                initForm("coolingZoneDrawer", {
                    label: "",
                    autoGenerateLabel: true,
                    selectedStorey: null,
                    selectedRooms: [],
                    selectedWalls: [],
                    isFullyEnclosed: false,
                    measurements: {
                        totalFloorArea: 0,
                        totalWallPerimeter: 0,
                    },
                    requirements: {
                        hasMinArea: false,
                        hasWallOutlet: false,
                        hasOtherElectrical: false,
                        hasVentilation: false,
                        ventilationType: null,
                    },
                    warnings: {},
                    systemServesZone: false,
                    upgradePackageId: "",
                });
            }
        }
    }, [isOpen, zoneIdToEdit]);

    const handleSubmit = async () => {
        const coolingZoneId = zoneIdToEdit ? zoneIdToEdit : `zone-${moment().format("YYYYMMDDHHmmssSS")}`;
        const coolingZoneData = {
            label: zoneLabel,
            autoGenerateLabel,
            selectedStorey,
            selectedRooms,
            selectedWalls,
            isFullyEnclosed,
            measurements: measurements,
            requirements: requirements,
            warnings: warnings,
            systemServesZone,
            upgradePackageId,
        };
        change(`model.modelData.coolingZones.${coolingZoneId}`, coolingZoneData);

        onClose();
        toggleSaving(false);
    };

    useEffect(() => {
        if (saving) {
            handleSubmit();
        }
    }, [saving]);

    return (
        <Drawer
            open={isOpen}
            title={zoneIdToEdit ? "Edit Cooling Zone" : "Create Cooling Zone"}
            subtitle={zoneIdToEdit ? "Edit the details of an existing cooling zone." : "Create a new cooling zone for your model."}
            close={onClose}
            classes={{
                paperClass: classes.drawerPaper,
            }}
            headerClassName={classes.drawerHeader}
            bodyClass={classes.drawerBody}
        >
            <ZoneForm
                accessor="newForm.coolingZoneDrawer"
                change={change}
                displayUnits={displayUnits}
                hasAllRequirements={hasAllRequirements}
            />

            {/* Buttons for the drawer */}
            <div className={classes.drawerButtonWrapper}>
                <div className={`${globalStyles.buttons}`} style={{ marginTop: "0rem" }}>
                    <Button large type="hollow" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button large onClick={() => toggleSaving(true)} disabled={saving || !hasAllRequirements}>
                        {zoneIdToEdit
                            ? (saving ? "Saving..." : "Save Changes")
                            : (saving ? "Creating..." : "Create")
                        }
                    </Button>
                </div>
            </div>
        </Drawer>
    )
};

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    resetForm: (formId) => dispatch(resetForm(formId)),
    initForm: (formId, values) => dispatch(initForm(formId, values)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateZoneDrawer);